import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import loadGoogleMaps, { getGoogleMapsApiKey } from '../../modules/google-maps-loader'
import styled from 'styled-components'
import GoogleMap from '../components/GoogleMap'
import { colors } from '../../search-common/settings'
import mapStyles from '../mapStylesNoText.json'
import staticMapParser from '../../modules/static-map-parser'
import { translate } from '../../modules/helper-functions'

const GOOGLE_MAPS_API_KEY = getGoogleMapsApiKey()

const Container = styled.div`
  position: relative;
  transition: max-height 0.1s ease-in-out;
  overflow: hidden;
  background-color: ${colors.grayTone1};
  height: ${( props ) => props.showMap ? '60vh' : 'auto'};;
  max-height: ${( props ) => props.showMap ? '60vh' : '300px'};
  margin: 0 20px;
`

/**
 * MapContainer component.
 */
class MapContainer extends Component {
  /**
   * Class constructor
   */
  constructor( props ) {
    super( props )

    this.state = {
      googleMaps: null,
      loaded: false
    }
  }

  /**
   * initMap
   */
  initMap() {
    loadGoogleMaps({ key: GOOGLE_MAPS_API_KEY })
      .then(( googleMaps ) => {
        this.setState({ googleMaps: googleMaps, loaded: true })
        return googleMaps
      })
  }

  /**
   * Get the query location as string.
   */
  getQueryLocationString() {
    const { cities, districts, location, query } = this.props
    let locationString = ''

    if ( cities.length === 1 ) {
      locationString = cities[0]
    }

    if ( districts.length === 1 ) {
      locationString = `${locationString}, ${districts[0]}`
    }

    if ( location.length > 0 ) {
      locationString = location.charAt( 0 ).toUpperCase() + location.slice( 1 )
    }

    if ( query.length > 0 ) {
      locationString = query.charAt( 0 ).toUpperCase() + query.slice( 1 )
    }

    // Add spaces after commas
    const re = /,/gi
    locationString = locationString.replace( re, ', ' )

    return locationString
  }

  /**
   * Method to retrieve static map url.
   */
  getStaticMapUrl() {
    const { cities } = this.props
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap'
    const center = cities.length === 1 ? cities[0] : 'Helsinki'
    const zoom = 13
    const size = [ 640, 320 ]
    const styles = staticMapParser.parse( JSON.stringify( mapStyles ))
    return `${baseUrl}?center=${center},Finland&zoom=${zoom}&size=${size.join( 'x' )}&scale=2&${styles}&key=${GOOGLE_MAPS_API_KEY}`
  }

  /**
   * Do stuff when component did get mounted.
   */
  componentDidMount() {
    if ( this.props.showMap ) {
      this.initMap()
    }
  }

  /**
   * Do stuff when component did update.
   *
   * @param {object} prevProps Previous props.
   */
  componentDidUpdate( prevProps ) {
    if ( this.props.showMap && !prevProps.showMap && !this.state.loaded ) {
      this.initMap()
    }
  }

  /**
   * Render method.
   */
  render() {
    const { showMap, translations } = this.props
    const { loaded, googleMaps } = this.state
    const hasQueryString = this.getQueryLocationString().length > 0

    return (
      <Container showMap={showMap} showContainer={ hasQueryString }>
        {showMap
          && (
            loaded
              ? <GoogleMap googleMaps={googleMaps} />
              : <h1>{translate( translations, 'Loading...' )}</h1>
          )
        }
      </Container>
    )
  }
}

MapContainer.propTypes = {
  results: PropTypes.array.isRequired,
  showMap: PropTypes.bool.isRequired,
  cities: PropTypes.array.isRequired,
  districts: PropTypes.array.isRequired,
  translations: PropTypes.object.isRequired,
  location: PropTypes.string,
  query: PropTypes.string,
}

MapContainer.defaultProps = {
  location: '',
  query: '',
}

/**
 * Map the redux state to component props.
 *
 * @param {object} state
 * @return {object}
 */
const mapStateToProps = ( state ) => ({
  results: state.search.results,
  cities: state.search.filters.cities,
  districts: state.search.filters.districts,
  translations: state.translations,
  location: state.search.filters.location,
  query: state.search.query
})

export default connect( mapStateToProps )( MapContainer )
